import { useEffect, useState } from "react";

export function Hero() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <section
      id="top"
      className="relative flex items-center pt-24 pb-12 md:pt-28 md:pb-16 lg:min-h-[calc(100svh-4.5rem)]"
    >
      {/* Faint background वेद mark, pushed behind text on the left */}
      <span className="hero-mark font-deva" aria-hidden>
        वेद
      </span>

      <div className="container-edit relative w-full">
        <div className="grid grid-cols-12 gap-y-12 lg:gap-x-8 items-center">
          {/* Left — eyebrow + headline + lede + CTAs */}
          <div className="col-span-12 lg:col-span-7 order-2 lg:order-1">
            <p
              className={`eyebrow flex items-center gap-3 mb-7 transition-all duration-1000 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
              }`}
              style={{ transitionDelay: "150ms" }}
            >
              <span className="inline-block w-8 h-px bg-[var(--color-violet)]" />
              <span>Since 2010 — A worldwide community</span>
            </p>

            <h1 className="font-display text-[clamp(2.5rem,7.2vw,5.75rem)] leading-[0.96] tracking-[-0.02em]">
              <span
                className={`block transition-all duration-1000 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: "250ms" }}
              >
                Uniting the world
              </span>
              <span
                className={`block transition-all duration-1000 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: "400ms" }}
              >
                through the{" "}
                <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
                  power
                </span>
              </span>
              <span
                className={`block transition-all duration-1000 ${
                  mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: "550ms" }}
              >
                of the Vedas.
              </span>
            </h1>

            <div
              className={`mt-9 max-w-[46ch] transition-all duration-1000 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: "750ms" }}
            >
              <hr className="rule-violet w-12 mb-5" />
              <p className="text-[1.05rem] md:text-[1.125rem] leading-[1.55] text-[var(--color-ink-soft)]">
                Preserving and sharing the living heritage of the Vedas —
                through chanting, study, sādhanā, and sacred ceremony — open to
                seekers of every tradition and every walk of life.
              </p>
            </div>

            <div
              className={`mt-9 flex flex-wrap items-center gap-x-7 gap-y-4 transition-all duration-1000 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: "900ms" }}
            >
              <a href="/register" className="btn-primary">
                <span>Begin learning</span>
                <span className="btn-arrow-icon" aria-hidden>
                  →
                </span>
              </a>
              <a href="#mission" className="btn-ghost">
                <span>Our mission</span>
                <span className="btn-arrow-icon" aria-hidden>
                  →
                </span>
              </a>
            </div>
          </div>

          {/* Right — logo + motto */}
          <aside
            className={`col-span-12 lg:col-span-5 order-1 lg:order-2 transition-all duration-1000 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
            style={{ transitionDelay: "350ms" }}
          >
            <div className="flex flex-col items-center lg:items-end gap-8 lg:gap-10">
              <div className="hero-logo-wrap">
                <img
                  src="/logo.svg"
                  alt="Veda Union"
                  className="hero-logo"
                  loading="eager"
                  decoding="async"
                />
              </div>

              <div className="text-center lg:text-right">
                <p className="font-display-italic text-[1.4rem] md:text-[1.65rem] leading-[1.2] tracking-tight">
                  Unity. Purity.{" "}
                  <span style={{ color: "var(--color-violet)" }}>Divinity.</span>
                </p>
                <p className="font-grotesk text-[0.7rem] tracking-[0.22em] uppercase text-[var(--color-ink-mute)] mt-3">
                  the motto
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Scroll affordance */}
      <a
        href="#mission"
        aria-label="Scroll to mission"
        className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-3 group"
      >
        <span className="font-grotesk text-[0.65rem] tracking-[0.3em] uppercase text-[var(--color-ink-mute)] group-hover:text-[var(--color-violet)] transition-colors">
          Read on
        </span>
        <span className="block w-px h-10 bg-[var(--color-rule-strong)] origin-top relative overflow-hidden">
          <span
            className="absolute inset-0 bg-[var(--color-violet)] origin-top"
            style={{ animation: "scroll-pulse 2.4s ease-in-out infinite" }}
          />
        </span>
      </a>

      <style>{`
        @keyframes scroll-pulse {
          0%   { transform: scaleY(0); transform-origin: top; }
          50%  { transform: scaleY(1); transform-origin: top; }
          51%  { transform: scaleY(1); transform-origin: bottom; }
          100% { transform: scaleY(0); transform-origin: bottom; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="scroll-pulse"] { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
